import { useMemo } from "react";
import { useUser } from "../../contexts/UserContext";
import { useNavigate } from "react-router";
import { useScopeParams } from "@/hooks/useScopeParams";
import type { Notification } from "./notifications";
import { getNotificationValueText } from "./utils";

type SentenceCreatorProps = {
  notification: Notification;
  type?:
    | "discrepancy_assignee"
    | "discrepancy_status"
    | "discrepancy_resolution_status";
};

export default function SentenceCreator({
  notification,
  type,
}: SentenceCreatorProps) {
  const user = useUser();
  const navigate = useNavigate();
  const { buildUrl } = useScopeParams();

  const currentUserName = useMemo(() => {
    const { first_name = "", last_name = "" } = user.userInfo || {};
    return `${first_name} ${last_name}`.trim();
  }, [user.userInfo]);

  if (!notification?.notification_data) {
    return <div className="text-red-500">No notification data available</div>;
  }

  if (notification.type?.startsWith("review_")) {
    return (
      <div className="text-[#2B4752]" data-testid="notification-sentence">
        <div className="flex flex-wrap items-start leading-relaxed">
          <span>
            {notification.message}{" "}
            <span
              className="cursor-pointer font-bold text-blue-400 underline hover:text-blue-600"
              onClick={() => navigate(buildUrl("safety/medical-review"))}
              data-testid="notification-medical-review-link"
            >
              Open Medical Review
            </span>
          </span>
        </div>
      </div>
    );
  }

  if (notification.type === "note_mention") {
    const { entity_key, author_name, author_id } =
      notification.notification_data;

    // Only the trial-scoped profiles can be reached with the current scope's
    // buildUrl. Cross-trial site, investigator and portfolio-trial notes live
    // under portfolio scope, so those render without a link rather than with a
    // broken one.
    const [kind, ...rest] = String(entity_key ?? "").split(":");
    const entityId = rest.join(":");
    const notePath =
      kind === "subject" && entityId
        ? `subjects/overview/${encodeURIComponent(entityId)}/notes`
        : kind === "trial-site" && entityId
          ? `sites/profile/${encodeURIComponent(entityId)}/notes`
          : kind === "review-item" && entityId
            ? `safety/medical-review?item=${encodeURIComponent(entityId)}`
            : null;

    const taggedThemselves =
      Boolean(author_id) && String(author_id) === String(user.userInfo?.id);

    return (
      <div className="text-[#2B4752]" data-testid="notification-sentence">
        <div className="flex flex-wrap items-start leading-relaxed">
          <span>
            {taggedThemselves
              ? "You mentioned yourself in a note"
              : `${author_name || "Someone"} mentioned you in a note`}
            {notePath ? (
              <>
                {" — "}
                <span
                  className="cursor-pointer font-bold text-blue-400 underline hover:text-blue-600"
                  onClick={() => navigate(buildUrl(notePath))}
                  data-testid="notification-note-link"
                >
                  Open note
                </span>
              </>
            ) : (
              "."
            )}
          </span>
        </div>
        {notification.message && (
          <div className="mt-0.5 text-[13px] italic text-[#6B7280]">
            “{notification.message}”
          </div>
        )}
      </div>
    );
  }

  if (notification.type === "insight_alert_triggered") {
    const { alert_title, insight_title } = notification.notification_data;
    return (
      <div className="text-[#2B4752]" data-testid="notification-sentence">
        <div className="flex flex-wrap items-start leading-relaxed">
          <span>
            Alert{" "}
            <span
              className="text-blue-400 underline cursor-pointer hover:text-blue-600 font-bold"
              onClick={() => navigate(buildUrl("alerts"))}
              data-testid="notification-alert-link"
            >
              {alert_title || "Untitled alert"}
            </span>{" "}
            was triggered on insight “{insight_title || "Unknown"}”.
          </span>
        </div>
      </div>
    );
  }

  const { actor, from_value, to_value, issue_id, field_label } =
    notification.notification_data;

  const actorText = actor?.name || "Unknown actor";
  const fromValueText = getNotificationValueText(field_label ?? "", from_value);
  const toValueText = getNotificationValueText(field_label ?? "", to_value);
  const issueIdText = issue_id || "Unknown discrepancy";
  const actionText = field_label || "Unknown action";

  const isActorCurrentUser =
    actorText.toLowerCase() === currentUserName.toLowerCase();
  const isToValueCurrentUser =
    toValueText.toLowerCase() === currentUserName.toLowerCase();

  const handleDiscrepancyClick = (issueId: string) => {
    navigate(buildUrl(`data/discrepancies?openIssue=${issueId}`));
  };

  return (
    <div className="text-[#2B4752]" data-testid="notification-sentence">
      {type === "discrepancy_assignee" && (
        <div className="flex flex-wrap items-start leading-relaxed">
          <span className="mr-1">{isActorCurrentUser ? "You" : actorText}</span>
          <span className="mr-1">
            changed the <strong>{actionText.toLowerCase()}</strong> of
            discrepancy
          </span>
          <span
            className="text-blue-400 underline cursor-pointer hover:text-blue-600 mr-1 font-bold"
            onClick={() => handleDiscrepancyClick(issueIdText)}
            data-testid={`notification-issue-link-${issueIdText}`}
          >
            {issueIdText}
          </span>
          <span>
            from {fromValueText} to {isToValueCurrentUser ? "you" : toValueText}
            .
          </span>
        </div>
      )}
      {type === "discrepancy_status" && (
        <div className="flex flex-wrap items-start leading-relaxed">
          <span className="mr-1">{isActorCurrentUser ? "You" : actorText}</span>
          <span className="mr-1">
            changed the <strong>{actionText.toLowerCase()}</strong> of
            discrepancy
          </span>
          <span
            className="text-blue-400 underline cursor-pointer hover:text-blue-600 mr-1 font-bold"
            onClick={() => handleDiscrepancyClick(issueIdText)}
            data-testid={`notification-issue-link-${issueIdText}`}
          >
            {issueIdText}
          </span>
          <span>
            from {fromValueText} to {toValueText}
          </span>
        </div>
      )}
      {type === "discrepancy_resolution_status" && (
        <div className="flex flex-wrap items-start leading-relaxed">
          <span className="mr-1">
            The <strong>{actionText.toLowerCase()}</strong> of discrepancy
          </span>
          <span
            className="text-blue-400 underline cursor-pointer hover:text-blue-600 mr-1 font-bold"
            onClick={() => handleDiscrepancyClick(issueIdText)}
            data-testid={`notification-issue-link-${issueIdText}`}
          >
            {issueIdText}
          </span>
          <span className="mr-1">
            that you are assigned to was changed from {fromValueText} to{" "}
            {toValueText}
          </span>
        </div>
      )}
    </div>
  );
}
